import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import Graphic from '@arcgis/core/Graphic.js';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import type Polyline from '@arcgis/core/geometry/Polyline.js';
import type Polygon from '@arcgis/core/geometry/Polygon.js';
import type Point from '@arcgis/core/geometry/Point.js';
import {
  SKETCH_FILL,
  SKETCH_FILL_HIGHLIGHT,
  SKETCH_LINE,
  SKETCH_LINE_HIGHLIGHT,
  SKETCH_POINT,
  SKETCH_POINT_HIGHLIGHT,
} from '@/map/symbols';
import { formatArea, formatDistance } from '@/state/measurementsStore';
import {
  nextSketchId,
  useSketchesStore,
  type SketchEntry,
  type SketchShape,
} from '@/state/sketchesStore';
import { getMapView } from './mapStore';

export const SKETCHES_LAYER_ID = '__rcmc_sketches';

let layer: GraphicsLayer | null = null;

export function ensureSketchesLayer(): GraphicsLayer {
  if (layer) return layer;
  layer = new GraphicsLayer({
    id: SKETCHES_LAYER_ID,
    title: 'Sketches',
    listMode: 'hide',
  });
  return layer;
}

export function getSketchesLayer(): GraphicsLayer | null {
  return layer;
}

function symbolFor(geometryType: __esri.Geometry['type']) {
  if (geometryType === 'point' || geometryType === 'multipoint') return SKETCH_POINT;
  if (geometryType === 'polyline') return SKETCH_LINE;
  return SKETCH_FILL;
}

function highlightSymbolFor(geometryType: __esri.Geometry['type']) {
  if (geometryType === 'point' || geometryType === 'multipoint') return SKETCH_POINT_HIGHLIGHT;
  if (geometryType === 'polyline') return SKETCH_LINE_HIGHLIGHT;
  return SKETCH_FILL_HIGHLIGHT;
}

function describeShape(shape: SketchShape): string {
  switch (shape) {
    case 'polygon':
      return 'Polygon';
    case 'rectangle':
      return 'Rectangle';
    case 'circle':
      return 'Circle';
    case 'polyline':
      return 'Polyline';
    case 'point':
      return 'Point';
  }
}

export function commitSketch(args: {
  shape: SketchShape;
  geometry: Geometry;
}): SketchEntry {
  const target = ensureSketchesLayer();
  const id = nextSketchId();
  const geometryType = args.geometry.type;
  const symbol = symbolFor(geometryType);

  const graphic = new Graphic({
    geometry: args.geometry,
    symbol,
    attributes: { __sketch_id: id },
  });
  target.add(graphic);

  let areaMeters: number | undefined;
  let lengthMeters: number | undefined;
  let point: [number, number] | undefined;
  let label = describeShape(args.shape);

  try {
    if (geometryType === 'polygon') {
      areaMeters = Math.abs(
        geometryEngine.geodesicArea(args.geometry as Polygon, 'square-meters'),
      );
      label = `${describeShape(args.shape)} · ${formatArea(areaMeters)}`;
    } else if (geometryType === 'polyline') {
      lengthMeters = geometryEngine.geodesicLength(args.geometry as Polyline, 'meters');
      label = `Polyline · ${formatDistance(lengthMeters)}`;
    } else if (geometryType === 'point') {
      const p = args.geometry as Point;
      point = [Number(p.longitude ?? p.x), Number(p.latitude ?? p.y)];
      label = `Point · ${point[0].toFixed(4)}, ${point[1].toFixed(4)}`;
    }
  } catch {
    // keep default label
  }

  const entry: SketchEntry = {
    id,
    shape: args.shape,
    geometry: args.geometry,
    graphic,
    areaMeters,
    lengthMeters,
    point,
    label,
  };
  useSketchesStore.getState().add(entry);
  return entry;
}

export function removeSketch(id: string): void {
  const target = layer;
  const entry = useSketchesStore.getState().entries.find((e) => e.id === id);
  if (entry && target) target.remove(entry.graphic);
  useSketchesStore.getState().remove(id);
}

export function clearSketches(): void {
  const target = layer;
  if (target) target.removeAll();
  useSketchesStore.getState().clear();
}

export function flashSketch(id: string): void {
  const view = getMapView();
  const entry = useSketchesStore.getState().entries.find((e) => e.id === id);
  if (!entry) return;

  const baseSymbol = symbolFor(entry.geometry.type);
  const highlight = highlightSymbolFor(entry.geometry.type);
  entry.graphic.symbol = highlight;
  window.setTimeout(() => {
    entry.graphic.symbol = baseSymbol;
  }, 900);

  if (view) {
    const extent = (entry.geometry as { extent?: __esri.Extent }).extent;
    if (extent) {
      void view.goTo(extent.expand(1.4)).catch(() => undefined);
    } else if (entry.geometry.type === 'point') {
      void view.goTo({ target: entry.geometry, zoom: Math.max(view.zoom ?? 14, 14) }).catch(
        () => undefined,
      );
    }
  }
}

export type { SketchEntry, SketchShape } from '@/state/sketchesStore';
